import React, { useEffect, useState } from 'react';
import {
  getItemReservations,
  type ItemReservationsResponse,
} from '@/api/item.api';

interface ItemReservationsModalProps {
  itemId: string | null;
  itemName?: string;
  itemCode?: string;
  onClose: () => void;
}

export default function ItemReservationsModal({
  itemId,
  itemName,
  itemCode,
  onClose,
}: ItemReservationsModalProps) {
  const [data, setData] = useState<ItemReservationsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!itemId) return;
    setIsLoading(true);
    setError(null);
    getItemReservations(itemId)
      .then((res) => {
        if (res.success && res.data) {
          setData(res.data);
        } else {
          setError(res.message || 'Failed to load reservation details');
        }
      })
      .catch((err) => {
        setError(err.message || 'Server error occurred');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [itemId]);

  if (!itemId) return null;

  const item = data?.item;
  const prodRes = data?.productionReservations || [];
  const salesAlloc = data?.salesAllocations || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-indigo-300">
              <i className="ri-lock-line text-xl" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                  Stock Reservations & Usage
                </span>
                {itemCode && (
                  <span className="text-xs font-mono text-slate-300">
                    {itemCode}
                  </span>
                )}
              </div>
              <h2 className="text-lg font-bold text-white mt-0.5">
                {itemName || item?.name || 'Material Reservations'}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto space-y-5">
          {isLoading ? (
            <div className="py-16 text-center space-y-3">
              <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto" />
              <p className="text-sm text-slate-500">Checking reservations across Production and Sales orders...</p>
            </div>
          ) : error ? (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-center gap-2">
              <i className="ri-error-warning-line text-base" />
              {error}
            </div>
          ) : (
            <>
              {/* Summary Metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                  <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    Total On-Hand Stock
                  </p>
                  <p className="text-xl font-bold text-slate-800 mt-1">
                    {item?.stock ?? 0}{' '}
                    <span className="text-xs font-normal text-slate-500">
                      {item?.unit_name || 'Units'}
                    </span>
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-amber-50/70 border border-amber-200">
                  <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wider flex items-center gap-1">
                    <i className="ri-lock-line" /> Total Reserved
                  </p>
                  <p className="text-xl font-bold text-amber-800 mt-1">
                    {item?.totalReserved ?? 0}{' '}
                    <span className="text-xs font-normal text-amber-600">
                      {item?.unit_name || 'Units'}
                    </span>
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-emerald-50/70 border border-emerald-200">
                  <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wider flex items-center gap-1">
                    <i className="ri-checkbox-circle-line" /> Available for Use
                  </p>
                  <p className="text-xl font-bold text-emerald-800 mt-1">
                    {item?.availableQty ?? 0}{' '}
                    <span className="text-xs font-normal text-emerald-600">
                      {item?.unit_name || 'Units'}
                    </span>
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-indigo-50/70 border border-indigo-200">
                  <p className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wider">
                    Active Reservations
                  </p>
                  <p className="text-xl font-bold text-indigo-800 mt-1">
                    {data?.summary.totalReservations ?? 0}{' '}
                    <span className="text-xs font-normal text-indigo-600">
                      Orders
                    </span>
                  </p>
                </div>
              </div>

              {/* Section 1: Production Orders (Manufacturing Material Reservations) */}
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    <i className="ri-settings-4-line text-indigo-600" />
                    Production Orders Reserving This Material
                    <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">
                      {prodRes.length}
                    </span>
                  </h3>
                </div>

                {prodRes.length === 0 ? (
                  <div className="p-6 text-center border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                    <i className="ri-inbox-line text-3xl text-slate-300 block mb-1" />
                    <p className="text-sm text-slate-500 font-medium">
                      No active production orders are currently reserving this item.
                    </p>
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold">
                          <th className="py-2.5 px-3">PO Number</th>
                          <th className="py-2.5 px-3">Manufacturing Product</th>
                          <th className="py-2.5 px-3">PO Status</th>
                          <th className="py-2.5 px-3 text-right">Required</th>
                          <th className="py-2.5 px-3 text-right">Reserved (Hold)</th>
                          <th className="py-2.5 px-3 text-right">Issued</th>
                          <th className="py-2.5 px-3">Warehouse</th>
                          <th className="py-2.5 px-3">Reservation Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {prodRes.map((r) => {
                          const isFullyReserved = Number(r.reserved_qty) >= Number(r.required_qty);
                          return (
                            <tr key={r.id} className="hover:bg-slate-50/80 transition-colors">
                              <td className="py-2.5 px-3 font-mono font-semibold text-indigo-600">
                                {r.po_number}
                              </td>
                              <td className="py-2.5 px-3 font-medium text-slate-800">
                                {r.manufactured_product || '—'}
                              </td>
                              <td className="py-2.5 px-3">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                  r.po_status === 'IN_PROGRESS'
                                    ? 'bg-blue-100 text-blue-700'
                                    : r.po_status === 'CONFIRMED'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-slate-100 text-slate-700'
                                }`}>
                                  {r.po_status}
                                </span>
                              </td>
                              <td className="py-2.5 px-3 text-right font-medium text-slate-600">
                                {r.required_qty} {r.unit}
                              </td>
                              <td className="py-2.5 px-3 text-right font-bold text-amber-700">
                                {r.reserved_qty} {r.unit}
                              </td>
                              <td className="py-2.5 px-3 text-right text-slate-500">
                                {r.issued_qty > 0 ? `${r.issued_qty} ${r.unit}` : '0'}
                              </td>
                              <td className="py-2.5 px-3 text-slate-600">
                                {r.warehouse_name || 'Primary'}
                              </td>
                              <td className="py-2.5 px-3">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                  isFullyReserved || r.reservation_status === 'RESERVED'
                                    ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                    : r.reservation_status === 'PARTIAL'
                                    ? 'bg-orange-100 text-orange-800 border border-orange-300'
                                    : r.reservation_status === 'ISSUED'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-slate-100 text-slate-600'
                                }`}>
                                  {r.reservation_status || (isFullyReserved ? 'RESERVED' : 'PARTIAL')}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Section 2: Sales Order Allocations (if any) */}
              {salesAlloc.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2.5">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <i className="ri-shopping-cart-2-line text-emerald-600" />
                      Sales Orders Pending Dispatch
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
                        {salesAlloc.length}
                      </span>
                    </h3>
                  </div>

                  <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold">
                          <th className="py-2.5 px-3">SO Number</th>
                          <th className="py-2.5 px-3">Customer</th>
                          <th className="py-2.5 px-3">SO Status</th>
                          <th className="py-2.5 px-3 text-right">Ordered Qty</th>
                          <th className="py-2.5 px-3 text-right">Delivered Qty</th>
                          <th className="py-2.5 px-3 text-right">Pending to Dispatch</th>
                          <th className="py-2.5 px-3">Delivery Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {salesAlloc.map((so) => (
                          <tr key={so.id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-2.5 px-3 font-mono font-semibold text-emerald-700">
                              {so.so_number}
                            </td>
                            <td className="py-2.5 px-3 font-medium text-slate-800">
                              {so.customer_name || '—'}
                            </td>
                            <td className="py-2.5 px-3">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700">
                                {so.so_status}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-right font-medium text-slate-600">
                              {so.ordered_qty}
                            </td>
                            <td className="py-2.5 px-3 text-right text-slate-500">
                              {so.delivered_qty}
                            </td>
                            <td className="py-2.5 px-3 text-right font-bold text-amber-700">
                              {so.pending_qty}
                            </td>
                            <td className="py-2.5 px-3 text-slate-500">
                              {so.delivery_date ? new Date(so.delivery_date).toLocaleDateString() : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between shrink-0">
          <p className="text-xs text-slate-500">
            <i className="ri-information-line mr-1 text-slate-400" />
            Reserved materials are locked for confirmed Production Orders and cannot be used by new orders.
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-semibold transition-colors cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
